// 공격 계산 — offset 은 **착탄축**이다
//
//   impact_i = impact_main + offset_i
//   open_i   = impact_i − rallying_i − marching_i
//   r_i      = offset_i − rallying_i − marching_i        ← 시각과 무관한 상수
//
// 개시축으로 구현하면 **행군 차이가 착탄 간격보다 클 때만 조용히 틀린다.** 평범한
// 데이터에서는 결과가 맞아 보이므로 검증 데이터에 그 경우가 없으면 잡히지 않고,
// 그 경우가 바로 이 제품이 없애려는 실패 — strike 가 main 보다 늦게 도착하는 상황 —
// 의 정체다.
//
// 이 파일은 틀린 축을 계산하는 함수도 함께 갖는다(solveOnOpenAxis). 제품 경로에서는
// 쓰이지 않고 계산 원장 페이지가 사람에게 반례를 보여 줄 때만 쓴다. 두 줄을 나란히
// 놓아야 "이쪽이 맞다" 가 눈으로 보이고, 사용자는 코드를 읽지 않는다.

import {
  addDuration,
  type Duration,
  duration,
  type Instant,
  instant,
  plus,
  toMillis,
} from '../time/brand.ts'

export type RallyKind = 'main' | 'strike' | 'garrison'

/** 시각이 정해지기 전의 rally. 조립 단계는 전부 delta 라 절대 시각이 없다. */
export interface RallyDraft {
  id: string
  kind: RallyKind
  rallying: Duration
  marching: Duration
  /** 착탄축 간격. main 은 0 이고 sub 는 부호가 자유롭다. */
  offset: Duration
}

export interface SolvedRally extends RallyDraft {
  open: Instant
  impact: Instant
}

/**
 * 시각과 무관한 앵커 상수 `r_i = offset_i − rallying_i − marching_i`.
 *
 * 이 값이 시각에 안 걸리는 덕에 CT 는 손잡이를 만지기 전에 시퀀스의 상대 구조를
 * 완성할 수 있고, 손잡이 하나로 전체가 강체처럼 평행이동한다.
 */
export function anchorConstant(rally: RallyDraft): Duration {
  return duration(toMillis(rally.offset) - toMillis(rally.rallying) - toMillis(rally.marching))
}

/**
 * main 정확히 1개라는 정본의 필수 조건을 확인한다.
 *
 * 조용히 첫 rally 를 main 으로 삼는 구현은 impact_main 이 무엇을 가리키는지 정의되지
 * 않은 상태를 감춘다. 감춰진 채로 계산이 돌면 화면에는 그럴듯한 시각이 찍힌다.
 */
function requireSingleMain(rallies: readonly RallyDraft[]): RallyDraft {
  const mains = rallies.filter((rally) => rally.kind === 'main')
  if (mains.length === 0) {
    throw new RangeError('시퀀스에 main 이 없다 — main 은 정확히 1개가 필수다')
  }
  if (mains.length > 1) {
    throw new RangeError(
      `시퀀스에 main 이 ${String(mains.length)}개다 — main 은 정확히 1개가 필수다`,
    )
  }
  const main = mains[0]
  if (main === undefined) throw new RangeError('시퀀스에 main 이 없다')
  if (toMillis(main.offset) !== 0) {
    throw new RangeError(
      `main 의 착탄 간격이 0 이 아니다 (${String(toMillis(main.offset))}ms) — ` +
        'main 이 착탄축의 원점이라 0 이어야 한다',
    )
  }
  return main
}

/** 착탄축 풀이. **이것이 제품이 쓰는 계산이다.** */
export function solveOnImpactAxis(
  rallies: readonly RallyDraft[],
  impactMain: Instant,
): SolvedRally[] {
  requireSingleMain(rallies)
  return rallies.map((rally) => {
    const impact = addDuration(impactMain, rally.offset)
    const open = addDuration(impact, duration(-toMillis(plus(rally.rallying, rally.marching))))
    return { ...rally, open, impact }
  })
}

/**
 * 개시축 풀이 — **틀린 축이다. 반례를 사람에게 보여 줄 때만 쓴다.**
 *
 * 지우지 않고 남겨 둔 이유: 계산 원장 페이지가 착탄축 줄과 이 줄을 나란히 찍어야
 * 사용자가 왜 착탄축이어야 하는지를 눈으로 확인할 수 있다. 화면에서 지우면 그 확인이
 * "테스트가 초록이다" 라는 자기 진술로 돌아간다.
 */
export function solveOnOpenAxis(
  rallies: readonly RallyDraft[],
  impactMain: Instant,
): SolvedRally[] {
  const main = requireSingleMain(rallies)
  const openMain = addDuration(impactMain, duration(-toMillis(plus(main.rallying, main.marching))))
  return rallies.map((rally) => {
    const open = addDuration(openMain, rally.offset)
    const impact = addDuration(open, plus(rally.rallying, rally.marching))
    return { ...rally, open, impact }
  })
}

/**
 * 시간 손잡이 `a` 로부터 전체를 산출한다 — 가장 이른 개시를 `지금 + a` 에 놓는다.
 *
 *   min(open_i) = now + a  ⟹  impact_main = (now + a) − min(r_i)
 *
 * 손잡이를 `open_main` 이라 부르든 `min(open_i)` 라 부르든 결과가 같은 이유가 여기
 * 있다 — 시퀀스가 강체라 어느 시각 하나를 정하면 나머지가 전부 따라온다. 그래서
 * 별도의 `open_main` 입력 칸을 두지 않는다.
 *
 * **하한(a ≥ 6초)을 여기서 강제하지 않는다.** 자동 평행이동과 직접 입력 차단이 서로
 * 다른 처리라 lower-bound.ts 가 따로 가진다.
 */
export function solveFromHandle(
  rallies: readonly RallyDraft[],
  now: Instant,
  handle: Duration,
): SolvedRally[] {
  requireSingleMain(rallies)
  const earliestConstant = Math.min(
    ...rallies.map((rally) => toMillis(anchorConstant(rally))),
  )
  const impactMain = instant(toMillis(now) + toMillis(handle) - earliestConstant)
  return solveOnImpactAxis(rallies, impactMain)
}

/** 가장 이른 개시 시각. 손잡이가 실제로 놓는 자리가 이것이다. */
export function earliestOpen(solved: readonly SolvedRally[]): Instant {
  if (solved.length === 0) {
    throw new RangeError('빈 시퀀스에는 가장 이른 개시가 없다')
  }
  return instant(Math.min(...solved.map((rally) => toMillis(rally.open))))
}

/** 착탄순 정렬. CT 가 설계한 순서가 실제로 나오는지를 이 축으로 본다. */
export function byImpact(a: { impact: Instant }, b: { impact: Instant }): number {
  return toMillis(a.impact) - toMillis(b.impact)
}

/** 개시순 정렬. roster 가 자기 차례를 언제 준비해야 하는지의 축이다. */
export function byOpen(a: { open: Instant }, b: { open: Instant }): number {
  return toMillis(a.open) - toMillis(b.open)
}
